import React, { useRef } from "react";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
// Sous-chemins du paquet @tiptap/extension-list DEJA installe (transitif starter-kit, v3.23.6).
// NE JAMAIS installer les anciens paquets standalone task-list/task-item v2 (obsoletes, regression).
import { TaskList } from "@tiptap/extension-list/task-list";
import { TaskItem } from "@tiptap/extension-list/task-item";
import { ActionIcon, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPhoto, IconListCheck } from "@tabler/icons-react";
import axios from "axios";
import { api } from "../../../../utils/client";
import { MAX_UPLOAD_SIZE, TOO_LARGE_MESSAGE } from "../../../../utils/uploads";

type TiptapEditorProps = {
  value: string;
  onChange: (html: string) => void;
};

/**
 * Editeur Tiptap client-only (RESEARCH Pattern 4 / Pitfall 2).
 * - ProseMirror est instancie UNIQUEMENT dans le hook `useEditor` (cote client),
 *   jamais au top-level du module, et ne touche jamais window/document hors hooks.
 * - Charge en React.lazy depuis Form.tsx (defense-in-depth SSR).
 * - Upload des images du corps (D-12) : POST vers /admin/article_images via le
 *   client axios CSRF-aware, puis insertion de l'URL ActiveStorage retournee
 *   (jamais d'encodage inline du fichier dans le HTML).
 */
const TiptapEditor: React.FC<TiptapEditorProps> = ({ value, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const editor = useEditor({
    extensions: [
      // StarterKit fournit titres, gras, italique, barre, listes, citation, undo/redo.
      // heading.levels doit lister explicitement les niveaux utilisés — sinon TipTap
      // ne les reconnaît pas au parsing HTML et les repasse en <p> au rechargement.
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Image,
      Link.configure({ openOnClick: false }),
      // Liste de taches (D-12) — parite avec le rendu public .article-body.
      // nested: false => pas de taches imbriquees (D-10).
      TaskList,
      TaskItem.configure({ nested: false }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  const handleImageButton = () => fileInputRef.current?.click();

  const handleImageSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // On reset l'input pour autoriser la re-selection du meme fichier.
    event.target.value = "";
    if (!file || !editor) return;

    // Les champs image passent par un Dropzone qui filtre la taille ; cet
    // input est nu, donc sans ce garde-fou un fichier trop lourd partait au
    // serveur pour revenir en 422 apres un upload inutile.
    if (file.size > MAX_UPLOAD_SIZE) {
      notifications.show({
        color: "red",
        title: "Image",
        message: TOO_LARGE_MESSAGE,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post<{ url: string }>(
        "/admin/article_images",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      // D-12 : on insere l'URL ActiveStorage retournee, jamais le fichier encode inline.
      editor.chain().focus().setImage({ src: data.url }).run();
    } catch (error) {
      // Le serveur precise la cause en 422 (format refuse, taille) : la
      // masquer derriere un message generique obligeait a deviner. Les
      // autres statuts gardent le message neutre — le controleur journalise
      // le detail interne sans l'exposer.
      const serverMessage =
        axios.isAxiosError<{ error?: string }>(error) &&
        error.response?.status === 422
          ? error.response.data?.error
          : null;

      notifications.show({
        color: "red",
        title: "Image",
        message: serverMessage || "L'image n'a pas pu être envoyée. Réessayez.",
      });
    }
  };

  return (
    <RichTextEditor editor={editor}>
      <RichTextEditor.Toolbar sticky stickyOffset={60}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Strikethrough />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
          <RichTextEditor.H4 />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
          <RichTextEditor.Blockquote />
          {/* Controle TaskList custom (D-12) : toggle la liste de taches Tiptap. */}
          <RichTextEditor.Control
            onClick={() => editor?.chain().focus().toggleTaskList().run()}
            aria-label="Liste de tâches"
            title="Liste de tâches"
          >
            <Tooltip label="Liste de tâches" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                component="span"
              >
                <IconListCheck size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </RichTextEditor.Control>
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link />
          <RichTextEditor.Unlink />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          {/* Controle image custom : ouvre un file picker -> upload -> insertion URL. */}
          <RichTextEditor.Control
            onClick={handleImageButton}
            aria-label="Insérer une image"
            title="Insérer une image"
          >
            <Tooltip label="Insérer une image" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                component="span"
              >
                <IconPhoto size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </RichTextEditor.Control>
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Undo />
          <RichTextEditor.Redo />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>

      <RichTextEditor.Content />

      {/* Input fichier cache, declenche par le controle image custom (article_images). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        style={{ display: "none" }}
        onChange={handleImageSelected}
      />
    </RichTextEditor>
  );
};

export default TiptapEditor;
